import { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import Logo from '@/components/shared/Logo'
import { ASILIFLOW_MONTHLY_PRICE_LABEL } from '@/config/billing'

type FAQ = {
  id: string
  question: string
  answer: string
  category: string
}

const faqs: FAQ[] = [
  {
    id: '1',
    question: 'How do I get started with AsiliFlow?',
    answer: 'Sign up for a free 30-day trial at asiliflow.com/signup. No credit card required. Once registered, you can set up your agency information, add caregivers and clients, and start scheduling shifts. You can be operational within minutes.',
    category: 'Getting Started'
  },
  {
    id: '2',
    question: 'What does AsiliFlow manage?',
    answer: 'AsiliFlow handles core home care operations including client management, caregiver scheduling, shift documentation, authorization management, incident tracking, team communication, and audit trails.',
    category: 'Features'
  },
  {
    id: '3',
    question: 'How much does AsiliFlow cost?',
    answer: `AsiliFlow is ${ASILIFLOW_MONTHLY_PRICE_LABEL} per month for agencies with unlimited staff and unlimited clients. This includes all features, authorization tracking, compliance alerts, shift documentation, and email support. We offer a 30-day free trial with no credit card required.`,
    category: 'Pricing'
  },
  {
    id: '4',
    question: 'Is there a per-user fee?',
    answer: `No! Unlike other platforms that charge per caregiver or per client, AsiliFlow has no per-user fees. Whether you have 10 caregivers or 500 caregivers, you pay the same flat monthly rate of ${ASILIFLOW_MONTHLY_PRICE_LABEL}.`,
    category: 'Pricing'
  },
  {
    id: '5',
    question: 'Can caregivers use the app on their phones?',
    answer: 'Yes. AsiliFlow is mobile-friendly and works on smartphones, tablets, and computers. Caregivers can review schedules, document shifts, and report incidents from any device without downloading an app.',
    category: 'Mobile'
  },
  {
    id: '6',
    question: 'Is my data secure and HIPAA compliant?',
    answer: 'Absolutely. AsiliFlow uses bank-level AES-256 encryption for data at rest and TLS 1.3 for data in transit. We are fully HIPAA compliant and undergo regular security reviews. All client and caregiver data is securely isolated.',
    category: 'Security'
  },
  {
    id: '7',
    question: 'How does authorization tracking work?',
    answer: 'AsiliFlow tracks authorization hours and expiration dates in real time. Dashboards show remaining units, and automated alerts at 90, 60, 30, and expired milestones help your team stay ahead of renewals.',
    category: 'Authorizations'
  },
  {
    id: '8',
    question: 'What happens when an authorization expires?',
    answer: 'AsiliFlow sends automated reminders to administrators as authorizations approach expiration and flags expired authorizations clearly so teams can renew them quickly and avoid service disruption.',
    category: 'Authorizations'
  },
  {
    id: '9',
    question: 'Can I schedule recurring shifts?',
    answer: 'Yes! AsiliFlow supports one-time shifts and recurring schedules (daily, weekly, bi-weekly, monthly). Set up a recurring pattern once, and the system generates all shifts automatically while checking for conflicts.',
    category: 'Scheduling'
  },
  {
    id: '10',
    question: 'How do caregivers document shifts?',
    answer: 'Caregivers can add shift notes, document completed tasks, and flag incidents directly from their phone or computer. All documentation is timestamped and stored securely for review and audit purposes.',
    category: 'Visits'
  },
  {
    id: '11',
    question: 'Can I handle last-minute cancellations?',
    answer: 'Yes! When a shift is cancelled, you can post it as an Open Shift for available caregivers to pick up. The system notifies qualified caregivers and allows them to claim the shift quickly.',
    category: 'Scheduling'
  },
  {
    id: '12',
    question: 'What reports are available?',
    answer: 'AsiliFlow provides Shift Summary Reports, Authorization Utilization Reports, Caregiver Activity Reports, Incident Reports, and complete Audit Logs. Reports can be exported to PDF or CSV for operational review and audits.',
    category: 'Reporting'
  },
  {
    id: '13',
    question: 'How long does setup take?',
    answer: 'Most agencies are up and running within 24 hours. The initial setup (adding agency info, caregivers, and first clients) takes about 30 minutes. You can start scheduling shifts immediately after setup.',
    category: 'Getting Started'
  },
  {
    id: '14',
    question: 'What kind of support do you offer?',
    answer: 'Support is available directly through your AsiliFlow account. You can request help at any time, and our team typically responds within 24 hours. We also provide email support at support@getasili.com.',
    category: 'Support'
  },
  {
    id: '15',
    question: 'Can I cancel anytime?',
    answer: 'Yes! AsiliFlow has no long-term contracts. You can cancel your subscription anytime from your account settings. You will retain access until the end of your current billing period.',
    category: 'Billing'
  },
  {
    id: '16',
    question: 'Do you offer refunds?',
    answer: 'We offer a 30-day free trial so you can test AsiliFlow risk-free. If you are not satisfied within the first 30 days of your paid subscription, contact us for a full refund.',
    category: 'Billing'
  },
  {
    id: '17',
    question: 'Can I export my data?',
    answer: 'Yes! You can export all client data, caregiver data, shift records, and reports as CSV files anytime. You own your data and can download it with one click.',
    category: 'Data'
  },
  {
    id: '18',
    question: 'Can I manage multiple locations?',
    answer: 'Yes. You can manage multiple locations under a single agency account while keeping schedules, staff activity, and authorizations organized in one system.',
    category: 'Multi-Location'
  },
]

function Highlight({ text, query }: { text: string; query: string }) {
  if (!text || !query.trim()) return <>{text}</>

  try {
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const parts = text.split(new RegExp(`(${escaped})`, 'ig'))

    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={i} className="bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-200 rounded px-0.5">{part}</mark>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    )
  } catch {
    return <>{text}</>
  }
}

export default function Help() {
  const [queryStr, setQueryStr] = useState('')
  const [activeIndex, setActiveIndex] = useState(-1)
  const itemRefs = useRef<Array<HTMLDetailsElement | null>>([])

  const filtered = faqs.filter((faq) => {
    const q = queryStr.trim().toLowerCase()
    if (!q) return true
    return (
      faq.question.toLowerCase().includes(q) ||
      faq.answer.toLowerCase().includes(q) ||
      faq.category.toLowerCase().includes(q)
    )
  })

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!filtered.length) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => {
        const next = Math.min(i + 1, filtered.length - 1)
        setTimeout(() => itemRefs.current[next]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 0)
        return next
      })
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => {
        const prev = Math.max(i - 1, -1)
        if (prev >= 0) {
          setTimeout(() => itemRefs.current[prev]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 0)
        }
        return prev
      })
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault()
      const ref = itemRefs.current[activeIndex]
      if (ref) ref.open = !ref.open
    } else if (e.key === 'Escape') {
      setQueryStr('')
      setActiveIndex(-1)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <Logo size="lg" />
            </Link>
            <nav className="hidden sm:block">
              <ol className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <li><Link to="/" className="hover:text-gray-700 dark:hover:text-gray-300">Home</Link></li>
                <li>•</li>
                <li className="text-gray-700 dark:text-gray-300 font-medium">Help Center</li>
              </ol>
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pt-24">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3">
            Help Center
          </h1>
          <p className="text-gray-700 dark:text-gray-300">
            Browse FAQs or search for answers to common questions about AsiliFlow.
          </p>
        </div>

        {/* Search */}
        <div className="max-w-3xl mx-auto mb-10">
          <div className="relative">
            <input
              type="text"
              value={queryStr}
              onChange={(e) => {
                setQueryStr(e.target.value)
                setActiveIndex(-1)
              }}
              onKeyDown={onKeyDown}
              placeholder="Search help articles..."
              className="w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 dark:text-gray-100 px-4 py-3 pr-12 shadow-sm
                         focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent transition-shadow"
            />
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500 pointer-events-none"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.3-4.3M11 19a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
          </div>
          {queryStr && filtered.length > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
              Use ↑↓ to navigate, Enter to open, Esc to clear
            </p>
          )}
        </div>

        {/* Quick Links */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <Link
            to="/docs"
            className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm
                       transition-all hover:shadow-md hover:border-primary-200"
          >
            <div className="mb-2 w-8 h-1.5 bg-primary-600 rounded-full"></div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Documentation</h3>
            <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm">
              Setup guides and best practices
            </p>
          </Link>

          <Link
            to="/contact"
            className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm
                       transition-all hover:shadow-md hover:border-primary-200"
          >
            <div className="mb-2 w-8 h-1.5 bg-primary-600 rounded-full"></div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Contact Support</h3>
            <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm">
              Schedule or message our team
            </p>
          </Link>

          <Link
            to="/status"
            className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm
                       transition-all hover:shadow-md hover:border-primary-200"
          >
            <div className="mb-2 w-8 h-1.5 bg-primary-600 rounded-full"></div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">System Status</h3>
            <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm">
              Live uptime and service health
            </p>
          </Link>
        </section>

        {/* FAQs */}
        <section className="max-w-3xl mx-auto">
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 sm:p-8 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Frequently Asked Questions
            </h2>

            {filtered.length === 0 && queryStr ? (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400 font-medium">No results found for "{queryStr}"</p>
                <button
                  onClick={() => setQueryStr('')}
                  className="mt-4 text-sm font-semibold text-primary-600 hover:text-primary-700"
                >
                  Clear search
                </button>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-800">
                {filtered.map((faq, i) => (
                  <li key={faq.id} className="py-4">
                    <details
                      ref={(el) => { itemRefs.current[i] = el }}
                      className={`group transition-all ${activeIndex === i ? 'ring-2 ring-primary-600 rounded-lg p-2 -m-2' : ''}`}
                    >
                      <summary className="flex justify-between items-start cursor-pointer list-none hover:text-primary-600 transition-colors">
                        <span className="text-gray-900 dark:text-gray-100 font-medium flex-1 pr-4">
                          <Highlight text={faq.question} query={queryStr} />
                        </span>
                        <svg
                          className="w-5 h-5 text-gray-400 dark:text-gray-500 group-open:rotate-180 transition-transform flex-shrink-0 mt-0.5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                        </svg>
                      </summary>
                      <div className="mt-3 text-gray-700 dark:text-gray-300 leading-relaxed">
                        <Highlight text={faq.answer} query={queryStr} />
                      </div>
                    </details>
                  </li>
                ))}
              </ul>
            )}

            {filtered.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800">
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  Showing {filtered.length} {filtered.length === 1 ? 'article' : 'articles'}
                  {queryStr && ` for "${queryStr}"`}
                </p>
              </div>
            )}
          </div>

          {/* Contact CTA */}
          <div className="text-center mt-8">
            <p className="text-gray-700 dark:text-gray-300 font-medium">Still need help?</p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center mt-3 bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold
                         hover:bg-primary-700 transition-all shadow-sm hover:shadow-md"
            >
              Contact Support
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 py-6 text-sm text-gray-500 dark:text-gray-400 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} AsiliFlow, a product of Asili LLC. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-gray-700 dark:hover:text-gray-300">Terms</Link>
            <Link to="/privacy" className="hover:text-gray-700 dark:hover:text-gray-300">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
